/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Checkbox as AntCheckbox } from 'antd'
import React from 'react'
import { ErrorLabel } from '../ErrorLabel'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import Text from '../Text'
import { SerializedStyles } from '@emotion/react'

/**
 * Checkbox component that combines Ant Design's Checkbox with custom styling and error handling.
 * Provides a consistent checkbox interface across the application with support for labels,
 * hints, and error states.
 *
 * @example
 * ```tsx
 * <Checkbox
 *   label="Accept terms and conditions"
 *   hint="Please read our terms before accepting"
 *   name="terms"
 *   value={accepted}
 *   onChange={(e) => setAccepted(e.target.checked)}
 * />
 * ```
 */
interface CheckboxProps {
  /** The label text or node to display next to the checkbox */
  label: string | React.ReactNode
  /** Optional hint text to display below the label */
  hint?: string
  /** Whether the checkbox is disabled */
  disabled?: boolean
  /** The name of the field (used for error handling) */
  name: string
  /** The current value of the checkbox */
  value: boolean
  /** Handler called when the checkbox value changes */
  onChange: (e: CheckboxChangeEvent) => void
  /** Array of field validation errors */
  fieldErrors?: any[]
  /** Custom error object */
  customError?: Record<string, any>
  /** Optional CSS styles to apply to the checkbox */
  checkBoxStyle?: SerializedStyles
  /** Use extra small font size for the label */
  extraSmallFont?: boolean
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /**
   * Some, not all, of what this box stands for is ticked: a select-all's third
   * state. Shown as a dash, and read out as "mixed".
   */
  indeterminate?: boolean
  /**
   * The box's accessible name when it has no visible label — a grid's tick
   * column, where the column says what the boxes are for.
   */
  ariaLabel?: string
  /** Id for the checkbox input; the label text then names it for assistive tech */
  id?: string
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  disabled = false,
  name,
  value,
  onChange,
  fieldErrors = [],
  customError = {},
  checkBoxStyle,
  hint,
  extraSmallFont = false,
  tabIndex,
  indeterminate = false,
  ariaLabel,
  id,
}) => {
  // A box named by `ariaLabel` alone draws no label column beside it.
  const hasLabel =
    (label !== null && label !== undefined && label !== '') || hint

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 16px;
      `}
    >
      <AntCheckbox
        id={id}
        disabled={disabled}
        onChange={onChange}
        checked={value}
        indeterminate={indeterminate}
        aria-label={ariaLabel}
        tabIndex={tabIndex}
        css={[
          css`
            width: 100%;
            max-width: 16px;

            /* Style the unchecked checkbox with yellow border */
            .ant-checkbox-inner {
              width: 18px;
              height: 18px;
              border: 2px solid ${colors.PRIMARY};
              border-radius: 2px;
            }

            /* Checked state - yellow background with dark checkmark */
            &.ant-checkbox-checked .ant-checkbox-inner {
              background-color: ${colors.PRIMARY};
              border-color: ${colors.PRIMARY};
              border-width: 0;
            }

            /* Style the checkmark icon with dark color */
            &.ant-checkbox-checked .ant-checkbox-inner::after {
              content: '';
              display: table;
              position: absolute;
              top: 46%;
              left: 21.5%;
              width: 5.71428571px;
              height: 9.14285714px;
              transform: rotate(45deg) scale(1) translate(-50%, -50%);
              opacity: 1;
              transition: all 0.2s cubic-bezier(0.12, 0.4, 0.29, 1.46) 0.1s;
              border: 2px solid ${colors.SECONDARY_300};
              border-top: 0;
              border-left: 0;
            }

            /* Hover state for unchecked checkbox */
            &:not(.ant-checkbox-checked):hover .ant-checkbox-inner {
              border-color: ${colors.PRIMARY};
            }

            /* Hover state for checked checkbox - maintain solid yellow */
            &.ant-checkbox-checked:hover .ant-checkbox-inner {
              background-color: ${colors.PRIMARY};
              border-color: ${colors.PRIMARY};
            }

            /* Disabled state */
            &.ant-checkbox-disabled .ant-checkbox-inner {
              border-color: ${colors.NEUTRAL_50};
              background-color: ${colors.NEUTRAL_30};
            }

            /* Mixed: the checked fill, with a dash where the tick would be */
            .ant-checkbox-indeterminate .ant-checkbox-inner {
              background-color: ${colors.PRIMARY};
              border-color: ${colors.PRIMARY};
              border-width: 0;
            }
            .ant-checkbox-indeterminate .ant-checkbox-inner::after {
              width: 10px;
              height: 2px;
              border-radius: 1px;
              background-color: ${colors.SECONDARY_300};
            }
          `,
          checkBoxStyle,
        ]}
      />
      {hasLabel ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            flex: 1 0 0;
            width: 100%;
          `}
        >
          <Text
            variant={extraSmallFont ? 'paragraphXSmall' : 'paragraphSmall'}
            weight='regular'
            color={colors.WHITE}
          >
            {id ? (
              <label
                htmlFor={id}
                css={css`
                  cursor: ${disabled ? 'not-allowed' : 'pointer'};
                `}
              >
                {label}
              </label>
            ) : (
              label
            )}
          </Text>
          {hint && (
            <div
              css={css`
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
              `}
            >
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                {hint}
              </Text>
            </div>
          )}
        </div>
      ) : null}
      <ErrorLabel
        fieldErrors={fieldErrors}
        customError={customError}
        name={name}
      />
    </div>
  )
}

export default Checkbox
